import {produce} from 'immer';
import parseRangeFromFilterValue from '@/components/smart_multiinput/rangeParser';
import {ChartFilterType, filterFreeTextTypes, filterNumericalTypes, filterSelectionTypes} from '../utils/chartConstants';
import {AdditionalFilter, AllFields} from 'entities/chartFilters';

const mergeNumericalFilterValues = (
    localFieldsValue: AllFields[keyof AllFields][0]['value'],
    globalFieldsValue: AllFields[keyof AllFields][0]['value'],
): string => {
    const [localMin, localMax] = parseRangeFromFilterValue(localFieldsValue);
    const [globalMin, globalMax] = parseRangeFromFilterValue(globalFieldsValue);

    // Get the narrowest range
    let resMin: string | number = Math.max(localMin, globalMin);
    let resMax: string | number = Math.min(localMax, globalMax);

    if (resMin === -Infinity) {
        resMin = '*';
    }

    if (resMax === Infinity) {
        resMax = '*';
    }

    return `[${resMin} TO ${resMax}]`;
};

const mergeFreeTextFilterValues = (
    localFieldsValue: AllFields[keyof AllFields][0]['value'],
    globalFieldsValue: AllFields[keyof AllFields][0]['value'],
): string => (
    `(${localFieldsValue}) AND (${globalFieldsValue})`
);

const mergeLocalAndGlobalFilters = (
    localAllFields: AllFields = {},
    localAdditionalFilters: AdditionalFilter[] = [],
    globalAllFields: AllFields = {},
    globalAdditionalFilters: AdditionalFilter[] = [],
): [AllFields, AdditionalFilter[]] => {
    let finalFields = produce(localAllFields, (draft) => draft);
    let finalFilters = produce(localAdditionalFilters, (draft) => draft);

    Object.keys(globalAllFields).forEach((key) => {
        if (!globalAllFields[key].length) {
            return;
        }

        const {type} = <{ type: ChartFilterType }>globalAdditionalFilters.find((x) => x.value === `all_fields.${key}`);

        // If filter is present in both filter sets and has a value
        if (localAllFields && Object.keys(localAllFields).includes(key) && localAllFields[key].length) {
            // If filter is selection type
            if (filterSelectionTypes.includes(type)) {
                // Iterate over values and keep only the ones that are overlapping
                finalFields = produce(finalFields, (draft) => {
                    localAllFields[key].forEach(({value}, idx) => {
                        if (!globalAllFields[key].find((x) => x.value === value)) {
                            draft[key][idx] = {
                                label: 'Illegal conjunction',
                                value: '0',
                            };
                        }
                    });
                });
            } else if (filterNumericalTypes.includes(type)) {
                const result = mergeNumericalFilterValues(localAllFields[key][0].value, globalAllFields[key][0].value);
                finalFields = produce(finalFields, (draft) => {
                    draft[key][0] = {value: result};
                });
            } else if (filterFreeTextTypes.includes(type)) {
                const result = mergeFreeTextFilterValues(localAllFields[key][0].value, globalAllFields[key][0].value);
                finalFields = produce(finalFields, (draft) => {
                    draft[key][0] = {value: result};
                });
            }
        } else {
            // If not present, just add it
            const value = globalAllFields[key];
            finalFields = produce(finalFields, (draft) => {
                draft[key] = value;
            });
            const filterToAdd = <AdditionalFilter>globalAdditionalFilters.find((x) => x.value.split('.')[1] === key);
            finalFilters = produce(finalFilters, (draft) => {
                draft.push(filterToAdd);
            });
        }
    });

    return [finalFields, finalFilters];
};

export default mergeLocalAndGlobalFilters;
